import { toast } from "sonner";
import i18n from "@/i18n";
import { asTranslateFn, errorDescription, errorTitle, formatSupportId } from "@/lib/api/errorMessages";
import { ApiError } from "@/lib/api/errors";
import { markErrorHandled } from "@/lib/api/handledErrors";
import { markOutcomeUnknown } from "@/lib/api/sentRequest";

const t = asTranslateFn(i18n.t);

/** Error toast: frontend-owned title + support id; persists longer. */
export function toastApiError(error: unknown) {
  const title = errorTitle(error, t);
  const support = formatSupportId(error, t);
  const reason =
    error instanceof ApiError ? (error.details as { reason?: string } | undefined)?.reason : undefined;
  const detail = errorDescription(error, t);
  const description =
    reason === "idempotency_in_progress"
      ? t("errors.inProgress")
      : [detail, support].filter(Boolean).join(" · ") || undefined;
  toast.error(title, { description, duration: 8000 });
}

export function toastSuccess(message: string, description?: string) {
  toast.success(message, { description, duration: 3000 });
}

export function toastInfo(message: string, description?: string) {
  toast(message, { description, duration: 4000 });
}

/**
 * A dispatched write whose response was lost: one notice saying it may
 * already have happened (instead of a generic error toast), and the error is
 * marked so a confirm dialog closes rather than offering to confirm again.
 */
export function toastMaybeDone(
  error: unknown,
  message = t("common.maybeDone"),
  description = t("common.maybeDoneBody"),
) {
  markErrorHandled(error);
  markOutcomeUnknown(error);
  toastInfo(message, description);
}
